import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { secureGet } from "../../config/api";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import PageScroller from "../../components/PageScroller";
import Filter from "../../components/Filter";
import LoadingLogo from "../../components/LoadingLogo";
import SnackbarNotification from "../../components/SnackbarNotification";
import Rows from "../../components/Rows";

const RaportZones = () => {
  const { t } = useTranslation();
  const topRef = useRef(null);
  const history = useHistory();
  const [rapportZones, setRapportZones] = useState([]);
  const [inventories, setInventories] = useState([]);

  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [filters, setFilters] = useState({ needle: "", inventory: "" });

  const [pageNumber, setPageNumber] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(30);

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const showSnackbar = (message, severity = "success", manual = false) =>
    setSnackbar({ open: true, message, severity, manual });


  const filterOptions = [
    { name: "needle", type: "search" },
    {
      name: "inventory",
      type: "dropdown",
      label: t("fields.inventory.inventaire"),
      options: inventories.map((r) => ({ value: String(r.id), label: r.name })),
    },
  ];

  const formatPercent = (v) => {
    if (v == null || Number.isNaN(Number(v))) return "—";
    const pct = v <= 1 ? v * 100 : v;
    return `${Math.round(pct)} %`;
  };
  const columns = [
    {
      key: "name",
      label: t("fields.inventory.zone"),
      width: "10%",
    },
    {
      key: "status",
      label: t("columns.status"),
      width: "10%",
      render: (status) => t(`inventory_status.${status}`) || status,
    },
    {
      key: "zoneManagerName",
      label: t("fields.inventory.responsable"),
      width: "15%",
    },
    {
      key: "countedByName",
      label: t("fields.inventory.agentComptage"),
      width: "15%",
    },
    {
      key: "controlledByName",
      label: t("fields.inventory.agentControle"),
      width: "15%",
    },
    {
      key: "progressRate",
      label: t("fields.inventory.taux"),
      width: "12.5%",
      render: (progressRate) => <span style={{ color: "#6B78F8" }}>{formatPercent(progressRate)}</span>,
    },
    {
      label: t("fields.inventory.emplacementsTraités"),
      width: "12.5%",
      render: (value, row) => (
        <span>
          {row.treatedLocations != null && row.totalLocations != null
            ? `${row.treatedLocations}/${row.totalLocations}`
            : "—"}
        </span>
      ),
    },
    {
      key: "elapsedTime",
      label: t("fields.inventory.timeElapsed"),
      width: "10%",
      render: (elapsedTime) => {
        const hours = Math.floor(elapsedTime / 3600);
        const minutes = Math.floor((elapsedTime % 3600) / 60);
        return `${hours}h ${minutes}min`;
      },
    },
  ];

  const pageRows = (data) => (Array.isArray(data) ? data : data?.content ?? []);
  const toInt = (n, fallback) => (Number.isFinite(n) ? n : fallback);

  const fetchRapportZones = useCallback(
    async (page = pageNumber, size = pageSize, active = filters) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("size", String(size));

        const n = (active.needle || "").trim();
        if (n) params.set("needle", n);

        const inv = (active.inventory || "").trim();
        if (inv) params.set("inventoryId", inv);

        const res = await secureGet(`/inventory-zones?${params.toString()}`, showSnackbar, { signal });
        console.log(res);
        const pageData = res?.data ?? {};
        const content = pageRows(pageData);

        setRapportZones(content);
        console.log(rapportZones);
        setTotalPages(toInt(pageData.totalPages, 1));
        if (Number.isFinite(pageData.number)) setPageNumber(pageData.number);
        if (Number.isFinite(pageData.size)) setPageSize(pageData.size);
      } catch (error) {
        if (error.name !== "AbortError") console.error("Error fetching rapport zones:", error);
      } finally {
        setLoading(false);
      }
    },
    [filters, pageNumber, pageSize, signal]
  );

  useEffect(() => {
      (async () => {
        try {
          const r = await secureGet("/inventories?size=1000", showSnackbar, { signal });
          setInventories(pageRows(r?.data)); 
          await fetchRapportZones(0, pageSize, { needle: "", inventory: ""});
        } catch (e) {
          if (e.name !== "AbortError") console.error(e);
        }
      })();
      return () => abortController.abort();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); 

  const handleFilterChange = (name, value) => {
    const updated = { ...filters, [name]: value };
    setFilters(updated);
    setPageNumber(0);
    fetchRapportZones(0, pageSize, updated);
  };

  const resetFilters = () => {
    const reset = { needle: "", inventory: "" };
    setFilters(reset);
    setPageNumber(0);
    fetchRapportZones(0, pageSize, reset);
  };

  const handlePageChange = (p) => {
    setPageNumber(p);
    fetchRapportZones(p, pageSize, filters);
    topRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handlePageSizeChange = (n) => {
    const sizeNum = Number(n) || pageSize;
    setPageSize(sizeNum);
    setPageNumber(0);
    fetchRapportZones(0, sizeNum, filters);
  };

  const handleRowClick = (row) => {
    history.push({   
      pathname: `/rapport-zones/details/${row.id}`,
      state: { zone: row }
    });
  };

  return (
    <div ref={topRef}>
      <Helmet>
          <title>GrayFox - {t("titles.zonesReport")}</title>
      </Helmet>

      {/* Header */}
      <div className="flex items-center mb-[15px] justify-between">
        <h1 className="text-[40px] font-semibold">{t("titles.zonesReport")}</h1>
      </div>

          <Filter
            filters={filterOptions}
            onFilterChange={handleFilterChange}
            resetFilters={resetFilters}
            filterValues={filters}
          />

      {/* Content */}
      <div style={{ position: "relative", minHeight: "200px" }}>
        {loading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(255,255,255,0.8)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1,
            }}
          >
            <LoadingLogo />
          </div>
        )}

        <Rows
          columns={columns}
          data={rapportZones}
          onRowClick={handleRowClick}
        />
      </div>

      {/* Pagination */}
      <PageScroller
        pageNumber={pageNumber}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
      />

      {/* Snackbar */}
      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default RaportZones;
